<template>
  <div class="container-fluid h-100">
    <div class="row pt-5 pb-3 h-100">
      <div class="col-12 h-100 d-flex flex-column">
        <div class="mb-3 d-flex flex-row justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <h1 class="mb-0">{{ title }}</h1>
          </div>
          <div v-if="showStartButton">
            <b-button
              :variant="buttonVariant"
              :size="buttonSize"
              @click="startTransition"
            >
              <b-icon
                v-if="buttonIcon"
                :icon="activeStarterZoneTransition.icon || buttonIcon"
                :variant="activeStarterZoneTransition.color_variant || buttonIconVariant"
                aria-hidden="true"
              />
              {{ activeStarterZoneTransition.label || buttonText }}
            </b-button>
          </div>
        </div>

        <div class="pt-3 flex-fill">
          <object-grid
            :key="gridKey"
            :ref="objectGridRef"
            model-class="PerformanceDataRun"
            :flow-subtype="flowSubtype"
            :col-state-local-storage-key="localStorageKey"
            :exclude-fields="excludeFields"
            class="h-100"
            grid-max-height="100%"
          ></object-grid>
        </div>
      </div>
    </div>

    <b-modal
      v-model="transModalShowing"
      centered
      hide-footer
      :size="modalSize"
    >
      <FlowObjectTransitionForm
        v-if="flowObject && activeStarterZoneTransitionTrigger"
        :key="activeStarterZoneTransitionTrigger"
        :flowObject="flowObject"
        :trigger="activeStarterZoneTransitionTrigger"
        :hideCancel="false"
        :autoStart="true"
        :noReloadOnSuccess="true"
        :minHeight="modalMinHeight"
        @loading-start="handleTransitionEvent"
        @loading-end="handleTransitionEvent"
        @loading-error="handleTransitionEvent"
        @transition-start="handleTransitionEvent"
        @transition-error="handleTransitionEvent"
        @transition-end="handleTransitionEvent"
        @transition-success="handleTransitionSuccess"
        @cancel="handleTransitionCancel"
      />
    </b-modal>
  </div>
</template>

<script>
  import { FlowObject } from '@/components/flows/models/flowObject';
  import FlowObjectTransitionForm from '@/components/flows/components/FlowObjectTransitionForm.vue';
  import { errorHandling } from '@/js/custom-vue-mixins';

  export default {
    name: 'PerformanceSystemDetail',
    components: {
      FlowObjectTransitionForm,
    },
    mixins: [errorHandling],
    props: {
      systemId: {
        type: [String, Number],
        required: true,
      },
      flowSubtype: {
        type: String,
        default: null,
      },
      title: {
        type: String,
        default: '',
      },
      starterZone: {  // name of the zone for the start button transitions, if any
        type: String,
      },
      buttonText: {
        type: String,
        default: 'Start',
      },
      buttonVariant: {
        type: String,
        default: 'sharp-secondary',
      },
      buttonSize: {
        type: String,
        default: 'md',
      },
      buttonIcon: {
        type: String,
        default: 'lightning-charge-fill',
      },
      buttonIconVariant: {
        type: String,
        default: 'warning',
      },
      modalMinHeight: {
        type: String,
        default: '150px',
      },
      modalSize: {
        type: String,
        default: 'md',
      },
    },
    data() {
      return {
        flowObject: null,
        transModalShowing: false,
        gridKey: 0,
        objectGridRef: 'dataRunGrid',
        excludeFields: ['system'],
      };
    },
    computed: {
      localStorageKey() {
        const subtype = this.flowSubtype || this.systemId;
        return `data-run-list-${subtype}`;
      },
      transitionsLoaded() {
        return this.flowObject ? this.flowObject.isTransitionsLoaded : false;
      },
      starterZoneTransitions() {
        if (!this.flowObject || !this.starterZone) {
          return [];
        }
        return this.flowObject.getTransitionsForZone(this.starterZone);
      },
      activeStarterZoneTransition() {
        return this.starterZoneTransitions[0] || null;
      },
      activeStarterZoneTransitionTrigger() {
        return this.activeStarterZoneTransition ? this.activeStarterZoneTransition.trigger : null;
      },
      showStartButton() {
        return !!this.starterZone && !!this.activeStarterZoneTransition;
      },
    },
    mounted() {
      this.initFlowObject();
    },
    methods: {
      async initFlowObject() {
        this.flowObject = new FlowObject({
          id: this.systemId,
          model_class: 'PerformanceSystem',
          flowSubtype: this.flowSubtype,
        });
        await this.flowObject.fetchTransitions();
      },
      startTransition() {  // currently only the starter zone -- if you add more zones/transitions for things, gotta modify this
        this.transModalShowing = true;
      },
      async refreshAfterTransition() {
        await this.flowObject.fetchTransitions();
        this.gridKey += 1;
      },
      async handleTransitionSuccess() {
        this.transModalShowing = false;
        this.refreshAfterTransition();
      },
      // eslint-disable-next-line no-unused-vars
      handleTransitionEvent(payload) {},
      handleTransitionCancel() {
        this.transModalShowing = false;
      },
    },
  };
</script>

<style lang="scss" scoped></style>
